export type PlanKey = "free" | "lite" | "starter" | "pro" | "business" | "scale" | "enterprise";
export type BillingInterval = "monthly" | "annual";

export interface TierPrice {
  monthly: number | null;
  annual: number | null;
  annualYearly: number | null;
  stripePriceId: {
    monthly: string | null;
    annual: string | null;
  };
}

export interface TierConfig {
  key: PlanKey;
  name: string;
  price: TierPrice;
  maxPages: number;
  maxSites: number;
  instantModeTtl: number;
  connectedModeTtl: number;
  manualRefreshLimit: number;
  recrawlInterval: number;
  apiRefreshEndpoint: boolean;
  webhookSupport: boolean;
  priorityCrawlQueue: boolean;
  analyticsEnabled: boolean;
  trial: boolean;
  trialDays: number;
  contactSales: boolean;
  persona: string;
  features: string[];
  ctaLabel: string;
  badge: string | null;
  featured: boolean;
}

export const ANNUAL_DISCOUNT_PERCENT = 30;

export const TIERS: TierConfig[] = [
  {
    key: "free",
    name: "Free",
    price: {
      monthly: 0,
      annual: 0,
      annualYearly: 0,
      stripePriceId: { monthly: null, annual: null },
    },
    maxPages: 10,
    maxSites: 1,
    instantModeTtl: 86400,
    connectedModeTtl: 0,
    manualRefreshLimit: 0,
    recrawlInterval: 0,
    apiRefreshEndpoint: false,
    webhookSupport: false,
    priorityCrawlQueue: false,
    analyticsEnabled: false,
    trial: false,
    trialDays: 0,
    contactSales: false,
    persona: "For personal sites and small blogs",
    features: [
      "Up to 10 pages",
      "1 site",
      "Content updated daily",
      "Instant Mode (works on any site)",
      "AI-readable endpoints (llms.txt, Markdown)",
      "Community support",
    ],
    ctaLabel: "Start free",
    badge: null,
    featured: false,
  },
  {
    key: "lite",
    name: "Lite",
    price: {
      monthly: 19,
      annual: 13,
      annualYearly: 160,
      stripePriceId: {
        monthly: process.env.STRIPE_PRICE_LITE_MONTHLY ?? null,
        annual: process.env.STRIPE_PRICE_LITE_ANNUAL ?? null,
      },
    },
    maxPages: 100,
    maxSites: 1,
    instantModeTtl: 43200,
    connectedModeTtl: 0,
    manualRefreshLimit: 1,
    recrawlInterval: 604800,
    apiRefreshEndpoint: false,
    webhookSupport: false,
    priorityCrawlQueue: false,
    analyticsEnabled: true,
    trial: true,
    trialDays: 21,
    contactSales: false,
    persona: "For small sites and freelancers",
    features: [
      "Up to 100 pages",
      "1 site",
      "Content updated twice daily",
      "Manual refresh from dashboard",
      "Basic analytics",
      "Email support",
    ],
    ctaLabel: "Start 21-day trial",
    badge: null,
    featured: false,
  },
  {
    key: "starter",
    name: "Starter",
    price: {
      monthly: 49,
      annual: 34,
      annualYearly: 408,
      stripePriceId: {
        monthly: process.env.STRIPE_PRICE_STARTER_MONTHLY ?? null,
        annual: process.env.STRIPE_PRICE_STARTER_ANNUAL ?? null,
      },
    },
    maxPages: 500,
    maxSites: 2,
    instantModeTtl: 21600,
    connectedModeTtl: 3600,
    manualRefreshLimit: 1,
    recrawlInterval: 604800,
    apiRefreshEndpoint: false,
    webhookSupport: true,
    priorityCrawlQueue: false,
    analyticsEnabled: true,
    trial: true,
    trialDays: 21,
    contactSales: false,
    persona: "For growing sites and marketing teams",
    features: [
      "Up to 500 pages",
      "2 sites",
      "Content updated every 6 hours",
      "Manual refresh from dashboard",
      "Connected Mode (CMS plugin for real-time sync)",
      "Analytics dashboard",
      "Email support",
    ],
    ctaLabel: "Start 21-day trial",
    badge: null,
    featured: false,
  },
  {
    key: "pro",
    name: "Pro",
    price: {
      monthly: 149,
      annual: 104,
      annualYearly: 1248,
      stripePriceId: {
        monthly: process.env.STRIPE_PRICE_PRO_MONTHLY ?? null,
        annual: process.env.STRIPE_PRICE_PRO_ANNUAL ?? null,
      },
    },
    maxPages: 5000,
    maxSites: 5,
    instantModeTtl: 3600,
    connectedModeTtl: 1800,
    manualRefreshLimit: -1,
    recrawlInterval: 86400,
    apiRefreshEndpoint: true,
    webhookSupport: true,
    priorityCrawlQueue: false,
    analyticsEnabled: true,
    trial: true,
    trialDays: 30,
    contactSales: false,
    persona: "For agencies and content-heavy sites",
    features: [
      "Up to 5,000 pages",
      "5 sites",
      "Content updated every hour",
      "Unlimited manual refresh",
      "Full analytics + AI agent tracking",
      "API access — refresh content programmatically",
      "Priority support",
    ],
    ctaLabel: "Start 30-day trial",
    badge: "Most popular",
    featured: true,
  },
  {
    key: "business",
    name: "Business",
    price: {
      monthly: 399,
      annual: 279,
      annualYearly: 3348,
      stripePriceId: {
        monthly: process.env.STRIPE_PRICE_BUSINESS_MONTHLY ?? null,
        annual: process.env.STRIPE_PRICE_BUSINESS_ANNUAL ?? null,
      },
    },
    maxPages: 25000,
    maxSites: 10,
    instantModeTtl: 900,
    connectedModeTtl: 900,
    manualRefreshLimit: -1,
    recrawlInterval: 3600,
    apiRefreshEndpoint: true,
    webhookSupport: true,
    priorityCrawlQueue: false,
    analyticsEnabled: true,
    trial: true,
    trialDays: 21,
    contactSales: false,
    persona: "For enterprises and multi-brand publishers",
    features: [
      "Up to 25,000 pages",
      "10 sites",
      "Content updated every 15 minutes",
      "Real-time webhooks + API refresh endpoint",
      "White-label endpoints",
      "Dedicated Slack support",
      "SLA 99.9% uptime",
    ],
    ctaLabel: "Start 21-day trial",
    badge: null,
    featured: false,
  },
  {
    key: "scale",
    name: "Scale",
    price: {
      monthly: 899,
      annual: 629,
      annualYearly: 7548,
      stripePriceId: { monthly: null, annual: null },
    },
    maxPages: 100000,
    maxSites: 20,
    instantModeTtl: 300,
    connectedModeTtl: 300,
    manualRefreshLimit: -1,
    recrawlInterval: 900,
    apiRefreshEndpoint: true,
    webhookSupport: true,
    priorityCrawlQueue: true,
    analyticsEnabled: true,
    trial: false,
    trialDays: 0,
    contactSales: true,
    persona: "For large-scale publishers and platforms",
    features: [
      "Up to 100,000 pages",
      "20 sites",
      "Content updated every 5 minutes",
      "Multi-region caching",
      "Priority support + dedicated account manager",
      "SLA 99.95% uptime",
      "Quarterly business review",
    ],
    ctaLabel: "Contact sales",
    badge: null,
    featured: false,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: {
      monthly: null,
      annual: null,
      annualYearly: null,
      stripePriceId: { monthly: null, annual: null },
    },
    maxPages: Infinity,
    maxSites: Infinity,
    instantModeTtl: 300,
    connectedModeTtl: 300,
    manualRefreshLimit: -1,
    recrawlInterval: 900,
    apiRefreshEndpoint: true,
    webhookSupport: true,
    priorityCrawlQueue: true,
    analyticsEnabled: true,
    trial: false,
    trialDays: 0,
    contactSales: true,
    persona: "For organizations with custom requirements",
    features: [
      "Unlimited pages",
      "Unlimited sites",
      "Real-time content sync",
      "Custom SLA",
      "SSO/SAML",
      "Dedicated infrastructure",
      "White-glove onboarding",
    ],
    ctaLabel: "Contact sales",
    badge: null,
    featured: false,
  },
];

export function getTierByName(name: string): TierConfig {
  const tier = TIERS.find((t) => t.key === name);
  return tier ?? TIERS[0];
}

export function getTierByPriceId(priceId: string | null | undefined): TierConfig {
  if (!priceId) return TIERS[0];
  const tier = TIERS.find(
    (t) =>
      t.price.stripePriceId.monthly === priceId ||
      t.price.stripePriceId.annual === priceId,
  );
  return tier ?? TIERS[0];
}

export function tierNameFromPriceId(priceId: string | null | undefined): string {
  return getTierByPriceId(priceId).key;
}

const PAID_TIER_KEYS = ["lite", "starter", "pro", "business", "scale", "enterprise"] as const;

export function tierNameFromProductName(productName: string): string | null {
  const lower = productName.toLowerCase();
  for (const key of PAID_TIER_KEYS) {
    const pattern = new RegExp(`\\b${key}\\b`);
    if (pattern.test(lower)) return key;
  }
  return null;
}

export function recommendTier(pageCount: number | null): TierConfig {
  if (pageCount === null) return getTierByName("starter");
  if (pageCount <= 100) return getTierByName("lite");
  if (pageCount <= 500) return getTierByName("starter");
  if (pageCount <= 5000) return getTierByName("pro");
  if (pageCount <= 25000) return getTierByName("business");
  return getTierByName("scale");
}

export interface PlanRecommendation {
  tiers: [string, string, string];
  highlighted: string;
  reason: string;
}

export function getRecommendedTiers(
  pageCount: number,
  siteCount: number = 1,
): PlanRecommendation {
  if (siteCount > 5) {
    return {
      tiers: ["pro", "business", "scale"],
      highlighted: "business",
      reason: `${siteCount} sites need Business or higher`,
    };
  }

  if (siteCount > 2) {
    return {
      tiers: ["starter", "pro", "business"],
      highlighted: "pro",
      reason: `${siteCount} sites need Pro or higher`,
    };
  }

  if (pageCount <= 100) {
    return {
      tiers: ["lite", "starter", "pro"],
      highlighted: "lite",
      reason: `${pageCount} pages — Lite gets you started with room to grow`,
    };
  }

  if (pageCount <= 500) {
    return {
      tiers: ["lite", "starter", "pro"],
      highlighted: "starter",
      reason: `${pageCount} pages — Starter covers you with room to grow`,
    };
  }

  if (pageCount <= 5000) {
    return {
      tiers: ["starter", "pro", "business"],
      highlighted: "pro",
      reason: `${pageCount} pages — Pro covers your full site`,
    };
  }

  if (pageCount <= 25000) {
    return {
      tiers: ["pro", "business", "scale"],
      highlighted: "business",
      reason: `${pageCount} pages — Business covers everything`,
    };
  }

  return {
    tiers: ["business", "scale", "enterprise"],
    highlighted: "scale",
    reason: `${pageCount} pages — Scale handles large sites`,
  };
}

export function getRefreshLabel(tier: TierConfig): string {
  const ttl = tier.instantModeTtl;
  if (ttl === 0) return "N/A";
  if (ttl <= 300) return "Every 5 minutes";
  if (ttl <= 900) return "Every 15 minutes";
  if (ttl <= 3600) return "Every hour";
  if (ttl <= 21600) return "Every 6 hours";
  if (ttl <= 43200) return "Twice daily";
  if (ttl <= 86400) return "Daily";
  return "Weekly";
}

export function getFeatureList(tier: TierConfig): string[] {
  return tier.features;
}

export function formatTtl(seconds: number): string {
  if (seconds === 0) return "N/A";
  if (seconds < 60) return `${seconds} seconds`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hour${seconds / 3600 === 1 ? "" : "s"}`;
  return `${Math.round(seconds / 86400)} day${seconds / 86400 === 1 ? "" : "s"}`;
}

export function formatRecrawlInterval(seconds: number): string {
  if (seconds === 0) return "No automatic re-crawl";
  if (seconds < 3600) return `Every ${Math.round(seconds / 60)} minutes`;
  if (seconds < 86400) return `Every ${Math.round(seconds / 3600)} hour${seconds / 3600 === 1 ? "" : "s"}`;
  if (seconds < 604800) return "Daily";
  return "Weekly";
}

export function isPaidTrialPlan(key: string): boolean {
  const tier = getTierByName(key);
  return tier.trial;
}

export function isContactSalesPlan(key: string): boolean {
  const tier = getTierByName(key);
  return tier.contactSales;
}
